import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { utils } from '@yola/webapp-plugins';
import { i18next } from '@yola/ws-sdk';
import {
  constants as subscriptionManagerConstants,
  selectors as subscriptionSelectors,
  verifiers as subscriptionManagerVerifiers,
} from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import features from 'yola-editor/src/js/modules/features';
import beforeUnloadListener from 'yola-editor/src/js/utils/before-unload-listener';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import useUpsellSettings from 'yola-editor/src/js/modules/upsell/hooks/use-upsell-settings';
import useDomainsExtraPrice, {
  extraPrices,
} from 'yola-editor/src/js/modules/upsell/hooks/use-domains-extra-price';
import { PUBLISHING } from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import publishing from 'yola-editor/src/js/modules/publishing';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import actionIds from '../constants/domain-purchase-action-ids';
import B2CDomainPurchaseContainer from './b2c-domain-purchase-container';

const {
  SubscriptionType: { DOMAIN_SUBSCRIPTION },
  SubscriptionTerm: { ANNUAL, MONTHLY },
} = subscriptionManagerConstants;
const {
  constants,
  trackers: { trackEvent },
} = segment;

const defaultCaptionOverrides = {};

function B2cDomainPurchaseDialogContainer({
  triggerId,
  domainNameSuggestion = '',
  withFreeSubdomain = false,
  withPurchasedDomains = false,
  shouldPublishOnSuccess = false,
  captionsOverrides = defaultCaptionOverrides,
  premiumFeature = '',
  additionalPaymentParams = {},
  onClose = Function.prototype,
  onNext = null,
}) {
  const dispatch = useDispatch();
  const siteId = useSelector(getSiteId);
  const activeHostingSubscription = useSelector(subscriptionSelectors.getActiveHostingSubscription);
  const activeSubscriptions = useSelector(subscriptionSelectors.getActiveSubscriptions);
  const limits = features.accessors.getCurrentPackageFeatureLimits();

  const [featureFlags] = useFeatureFlags(['upgrade_to_publish']);

  const { upgrade_to_publish: isUpgradeToPublishEnabled } = featureFlags;

  const extraPriceType = useDomainsExtraPrice();
  const upsellSettings = useUpsellSettings({
    isFreeDomainAvailable: extraPriceType === extraPrices.NO_DISCOUNTS,
  });

  const customDomainFeatureAvailable = limits.customDomain.available;
  const isSubscriptionExpired =
    subscriptionManagerVerifiers.hasExpiredPaidSubscription(activeSubscriptions);
  const isPublishDomainAvailable = customDomainFeatureAvailable || isSubscriptionExpired;

  const showB2cDomainPurchaseDialog = () =>
    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.B2C_DOMAIN_PURCHASE, {
        triggerId,
        domainNameSuggestion,
        withFreeSubdomain,
        withPurchasedDomains,
        shouldPublishOnSuccess,
        captionsOverrides,
        premiumFeature,
        onClose,
      })
    );

  const redirectToPaymentPage = (domain, successUrl) => {
    beforeUnloadListener.remove();

    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    const paymentParams = {
      ...additionalPaymentParams,
      products: [DOMAIN_SUBSCRIPTION],
      term: ANNUAL,
      latitude_site_id: siteId,
      domain_name: domain,
      ...(activeHostingSubscription?.term === MONTHLY && { p1y_upgrade: true }),
      ...(successUrl && { success_url: successUrl }),
    };

    const paymentUrl = utils.generatePaymentUrl(paymentParams);

    saveSiteWithEffect(() => {
      redirectToUrl(paymentUrl);
    });
  };

  const showPaywall = ({ domain, ...restProps }) =>
    upsellSettings[premiumFeature === PUBLISHING ? 'publishing' : 'customDomain']
      .onUpgrade({
        ...(domain && { domainName: domain }),
        ...restProps,
      })
      .then(Function.prototype, showB2cDomainPurchaseDialog);

  const processPublishDomain = ({ domain, successUrl }) => {
    if (isSubscriptionExpired) {
      dispatch(
        dialogs.actions.show(dialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
          onClose: () => dispatch(publishing.actions.publishWebsite(domain)),
          additionalPaymentParams: {
            successUrl,
          },
        })
      );
      return;
    }

    dispatch(publishing.actions.publishWebsite(domain));
  };

  const handleSelect = ({ domain, actionId, successUrl }) => {
    switch (actionId) {
      case actionIds.BUY_DOMAIN: {
        const isPurchaseDomainAvailable = customDomainFeatureAvailable || isSubscriptionExpired;

        if (isPurchaseDomainAvailable) {
          redirectToPaymentPage(domain, successUrl);
          break;
        }

        showPaywall({
          domain,
          actionId,
          successUrl,
        });
        break;
      }

      case actionIds.GET_FREE_DOMAIN: {
        if (isUpgradeToPublishEnabled) {
          if (isPublishDomainAvailable) {
            processPublishDomain({ domain, successUrl });
          } else {
            showPaywall({ actionId, successUrl });
          }

          break;
        }

        dispatch(publishing.actions.publishWebsite());
        break;
      }

      case actionIds.EXISTING_DOMAIN: {
        if (isPublishDomainAvailable) {
          processPublishDomain({ domain, successUrl });
          break;
        }

        showPaywall({
          actionId,
          successUrl,
        });
        break;
      }

      case actionIds.THIRD_PARTY_DOMAIN: {
        if (customDomainFeatureAvailable) {
          dispatch(
            dialogs.actions.show(dialogs.dialogTypes.PENDING, {
              captions: {
                title: i18next.t('Loading...'),
              },
            })
          );

          redirectToUrl(successUrl);
          break;
        }

        if (isSubscriptionExpired) {
          dispatch(
            dialogs.actions.show(dialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
              additionalPaymentParams: {
                successUrl,
              },
            })
          );
          break;
        }

        showPaywall({
          actionId,
          successUrl,
          isConnectThirdPartyDomain: true,
        });
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleClose = () => {
    trackEvent(constants.events.DOMAIN_PURCHASE_DIALOG_CANCELED, {
      triggerId,
    });

    dispatch(dialogs.actions.hide());
    onClose();
  };

  let handleNext = null;

  if (onNext) {
    handleNext = () => {
      trackEvent(constants.events.DOMAIN_PURCHASE_NOT_NOW_CLICKED, {
        triggerId,
      });

      onNext();
    };
  }

  return (
    <B2CDomainPurchaseContainer
      view="dialog"
      triggerId={triggerId}
      domainNameSuggestion={domainNameSuggestion}
      withFreeSubdomain={withFreeSubdomain}
      withPurchasedDomains={withPurchasedDomains}
      shouldPublishOnSuccess={shouldPublishOnSuccess}
      captionsOverrides={captionsOverrides}
      premiumFeature={premiumFeature}
      onSelect={handleSelect}
      onClose={handleClose}
      onNext={handleNext}
    />
  );
}

B2cDomainPurchaseDialogContainer.propTypes = {
  triggerId: PropTypes.string.isRequired,
  domainNameSuggestion: PropTypes.string,
  withFreeSubdomain: PropTypes.bool,
  withPurchasedDomains: PropTypes.bool,
  shouldPublishOnSuccess: PropTypes.bool,
  captionsOverrides: PropTypes.shape({}),
  premiumFeature: PropTypes.string,
  additionalPaymentParams: PropTypes.object,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
};

export default B2cDomainPurchaseDialogContainer;
